import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';

import { IdService } from '../id.service';
import type { BackendError } from '../../domain/error.domain';

export const REST_FRAMEWORK_CONFIGURATION = new InjectionToken<RestFrameworkConfiguration>('restFrameworkConfiguration');

export interface RestFrameworkConfiguration {
  readonly restApiVersion?: string;
  readonly restConsumerName?: string;
  readonly extraHeaders?: { [key: string]: string };
  readonly taiAgentHack?: boolean;
}

/**
 * Adds HTTP headers required by rest framework such as correlation id and consumer name
 */
@Injectable({
  providedIn: 'root'
})
export class RestFrameworkService {

  private static readonly HEADER_REST_VERSION: string = 'x-broker-rest-version';
  private static readonly HEADER_REST_CONSUMER: string = 'x-consumer-name';
  private static readonly HEADER_CORRELATION_ID: string = 'x-correlation-id';
  private static readonly DEFAULT_REST_API_VERSION: string = 'v1.0';
  private static readonly DEFAULT_REST_CONSUMER_NAME: string = 'webbroker';
  private static readonly AJAX_REQUEST_INDICATOR_PARAM: string = 'AJAXREQUEST';

  constructor(@Optional() @Inject(REST_FRAMEWORK_CONFIGURATION)
  private readonly configuration?: RestFrameworkConfiguration) {
    if (this.configuration === undefined || this.configuration === null) {
      this.configuration = {
        restApiVersion: RestFrameworkService.DEFAULT_REST_API_VERSION,
        restConsumerName: RestFrameworkService.DEFAULT_REST_CONSUMER_NAME
      };
    }
  }

  addHeaders(httpHeaders: HttpHeaders): HttpHeaders {
    let headers = httpHeaders !== undefined ? httpHeaders : new HttpHeaders();
    const restApiVersion = this.configuration.restApiVersion !== undefined ?
      this.configuration.restApiVersion : RestFrameworkService.DEFAULT_REST_API_VERSION;
    const restConsumerName = this.configuration.restConsumerName !== undefined ?
      this.configuration.restConsumerName :
      RestFrameworkService.DEFAULT_REST_CONSUMER_NAME;
    headers = headers
      .set(RestFrameworkService.HEADER_CORRELATION_ID, IdService.getUUID())
      .set(RestFrameworkService.HEADER_REST_VERSION, restApiVersion)
      .set(RestFrameworkService.HEADER_REST_CONSUMER, restConsumerName);

    if (this.configuration.extraHeaders !== undefined) {
      Object.keys(this.configuration.extraHeaders).forEach((key: string): void => {
        headers = headers.set(key, this.configuration.extraHeaders[key]);
      });
    }
    return headers;
  }

  augmentParams(httpParams: HttpParams): HttpParams {
    let params = httpParams !== undefined ? httpParams : new HttpParams();

    if (this.configuration.taiAgentHack === true || this.configuration.taiAgentHack === undefined) {
      params = params.set(RestFrameworkService.AJAX_REQUEST_INDICATOR_PARAM, '1');
    }
    return params;
  }

  unwrapResponse<T>(response: Record<string, T>): Record<string, T> | T {
    if (response !== undefined && response.payload !== undefined) {
      return response.payload as Record<string, T>;  // unwrap the envelope and extract payload
    }
    return response;
  }

  getErrors<T>(response: Record<string, T>): Array<BackendError> {
    if (response !== undefined && response.errors !== undefined) {
      return response.errors as Array<BackendError>;
    }
    return [];
  }
}
